import classnames from "classnames";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { useEffect, useRef, useState } from "react";

import * as styles from "./Video.module.css";

const Video = ({
  video = "",
  src,
  posterVideo,
  posterSrc,
  posterImage,
  staticPoster,
  className,
  autoPlay,
  noBorderRadius,
  subtitles,
  autoSubtitles,
  label,
  noFilter,
  style,
  loadingFrame,
  altPlayIcon,
  type,
  description,
  lowerPlayButton,
  small,
  stop,
  darkOnLight,
  tint,
  noPauseButton
}) => {
  const mainVideo = useRef();
  const [videoPlaying, toggleVideoPlaying] = useState(autoPlay);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const prefersReducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    setPrefersReducedMotion(prefersReducedMotionQuery.matches);

    if (!prefersReducedMotionQuery.matches && autoPlay) {
      toggleVideoPlaying(true);
    }

    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches);
      if (event.matches) {
        toggleVideoPlaying(false);
      } else if (autoPlay) {
        toggleVideoPlaying(true);
      }
    };

    prefersReducedMotionQuery.addEventListener("change", handleChange);

    return () => {
      prefersReducedMotionQuery.removeEventListener("change", handleChange);
    };
  }, [autoPlay]);

  const handlePosterClick = (e) => {
    e.preventDefault();
    toggleVideoPlaying(true);
  };

  const handlePauseClick = () => {
    if (!mainVideo.current) return;
  
    if (mainVideo.current.paused) {
      mainVideo.current.play();
      toggleVideoPlaying(true);
    } else {
      mainVideo.current.pause();
      toggleVideoPlaying(false);
    }
  };

  const videoClasses = classnames(styles.video, className, {
    [styles.noBorderRadius]: noBorderRadius,
    [styles.noFilter]: noFilter,
    [styles.small]: small,
  });

  const playIconClasses = classnames(styles.playIcon, {
    [styles.altPlayIcon]: altPlayIcon,
    [styles.lowerPlayButton]: lowerPlayButton
  });

  let renderPoster;
  if (posterVideo || posterSrc) {
    renderPoster = (
      <video
        src={posterSrc || `https:${posterVideo}`}
        playsInline
        loop
        muted
        autoPlay
        preload="none"
      >
        <track kind="captions" />
      </video>
    );
  } else if (posterImage) {
    renderPoster = <GatsbyImage image={posterImage} alt="Video still" />;
  } else if (staticPoster) {
    renderPoster = staticPoster;
  } else {
    renderPoster = <div className={styles.noPoster} />;
  }

  if (prefersReducedMotion) {
    return (
      <div className={`${videoClasses} video-wrapper`}>
        <div className={styles.wrapper} style={style}>
          <div className={styles.poster}>
            {loadingFrame && (
              <img src={loadingFrame} className={styles.loadingFrame} alt="" />
            )}
            {renderPoster}
          </div>
        </div>
      </div>
    );
  }

  let renderInner;

  if (!videoPlaying && !autoPlay) {
    renderInner = (
      <div className={styles.poster}>
        {loadingFrame && (
          <img src={loadingFrame} className={styles.loadingFrame} alt="" />
        )}
        {renderPoster}
        <button
          className={styles.posterPlayIcon}
          onClick={handlePosterClick}
          type="button"
          aria-label="Play video"
        >
          <div className={playIconClasses} />
          <p className="h4" style={{ color: darkOnLight ? "black" : "white" }}>
            {label || "Play"}
          </p>
        </button>
      </div>
    );
  } else {
    renderInner = (
      <div className={styles.videoContainer}>
        <video
          onClick={(e) => e.preventDefault()}
          playsInline
          loop
          muted
          controls={!autoPlay}
          autoPlay={videoPlaying}
          className={styles.mainVideo}
          ref={mainVideo}
          preload="none"
          aria-label={description}
        >
          <source
            src={src || `https:${video}`}
            type={type === "webm" ? "video/webm" : "video/mp4"}
          />
          {!autoSubtitles && <track kind="subtitles" label="Off" default={true} />}
          {subtitles &&
            subtitles.map((item, i) => (
              <track
                default={autoSubtitles ? true : autoSubtitles === i ? true : false}
                kind="subtitles"
                label={item.description}
                srcLang={item.lang}
                src={item.publicUrl}
                key={`${video}-subtitle-${i + 1}`}
              />
            ))}
        </video>
  
        {/* Pause/Play Button (Appears Only When autoPlay is Enabled) */}
        {(autoPlay && noPauseButton !== true) && (
          <button
            className={styles.pauseButton}
            onClick={handlePauseClick}
            aria-label={videoPlaying ? "Pause video" : "Play video"}
          >
            <div className={videoPlaying ? styles.pauseIcon : styles.playIcon} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`${videoClasses} video-wrapper`}>
      <div className={styles.wrapper} style={style}>
        {renderInner}
      </div>
      {tint && <div className={styles.tint} />}
      {/* Pause Button for autoplay vids */}
      {(autoPlay && noPauseButton !== true) && (
        <button
          className={styles.autoPauseButton}
          onClick={handlePauseClick}
          aria-label={videoPlaying ? "Pause video" : "Play video"}
        >
          <div className={videoPlaying ? styles.autoPauseIcon : styles.autoPlayIcon} />
        </button>
      )}
    </div>
  );
};

export default Video;

export const query = graphql`
  fragment VideoFields on ContentfulVideo {
    video {
      file {
        url
      }
    }
    videoUrl
    poster {
      title
      file {
        url
        contentType
      }
      gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
    }
    subtitles {
      description
      file {
        url
      }
      publicUrl
    }
  }
`;
